import React from 'react';
import MarkdownText from '../components/MarkdownText';
import { Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  InputBase,
  IconButton,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Alert,
  Pagination,
  Container
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import apiService from '../services/ApiService';

/**
 * Search page component
 */
class SearchPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
      searchResults: [],
      loading: false,
      error: null,
      searchPerformed: false,
      currentPage: 1,
      resultsPerPage: 10,
      totalResults: 0
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
  }

  /**
   * Extract search query from URL params when component mounts
   */
  componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q') || '';
    const tag = urlParams.get('tag');
    
    if (query || tag) {
      this.setState({ searchQuery: query }, () => {
        if (tag) {
          this.searchByTag(tag);
        } else {
          this.performSearch();
        }
      });
    }
  }

  /**
   * Handle search input changes
   * @param {Object} event - Input change event
   */
  handleSearchChange(event) {
    this.setState({ searchQuery: event.target.value });
  }

  /**
   * Handle search form submission
   * @param {Object} event - Form submit event
   */
  handleSearchSubmit(event) {
    event.preventDefault();
    this.performSearch();
    
    // Update URL with search query
    const url = new URL(window.location);
    if (this.state.searchQuery) {
      url.searchParams.set('q', this.state.searchQuery);
    } else {
      url.searchParams.delete('q');
    }
    
    // Remove tag param if present
    url.searchParams.delete('tag');
    
    window.history.pushState({}, '', url);
  }

  /**
   * Perform search
   */
  async performSearch() {
    if (!this.state.searchQuery.trim()) {
      this.setState({
        error: 'Please enter a search query',
        searchResults: [],
        searchPerformed: false,
        currentPage: 1
      });
      return;
    }
    
    this.setState({ loading: true, error: null, currentPage: 1 });
    
    try {
      const response = await apiService.searchDocuments(this.state.searchQuery);
      this.setState({
        searchResults: response.data,
        loading: false,
        searchPerformed: true,
        currentPage: 1
      });
    } catch (error) {
      this.setState({
        error: 'Error searching documents. Please try again.',
        loading: false,
        searchPerformed: true,
        currentPage: 1
      });
    }
  }

  /**
   * Search documents by tag
   * @param {String} tag - Tag to search for
   */
  async searchByTag(tag) {
    this.setState({ loading: true, error: null, currentPage: 1 });
    
    try {
      const response = await apiService.getDocuments({ tag });
      this.setState({
        searchResults: response.data,
        loading: false,
        searchPerformed: true,
        currentPage: 1
      });
    } catch (error) {
      this.setState({
        error: 'Error searching by tag. Please try again.',
        loading: false,
        searchPerformed: true,
        currentPage: 1
      });
    }
  }

  /**
   * Format date to readable string
   * @param {String} dateString - ISO date string
   * @returns {String} Formatted date
   */
  formatDate(dateString) {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  /**
   * Highlight search terms in text
   * @param {String} text - Text to highlight
   * @param {String} query - Search query
   * @returns {String} Text with highlighted terms
   */
  highlightText(text, query) {
    if (!query || !text) return text;
    
    // Simple highlighting approach
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  /**
   * Handle page change for pagination
   * @param {Event} event - Page change event
   * @param {Number} page - New page number
   */
  handlePageChange(event, page) {
    this.setState({ currentPage: page });
  }

  /**
   * Get paginated results
   * @returns {Array} Paginated search results
   */
  getPaginatedResults() {
    const { searchResults, currentPage, resultsPerPage } = this.state;
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    return searchResults.slice(startIndex, endIndex);
  }

  /**
   * Get total number of pages
   * @returns {Number} Total pages
   */
  getTotalPages() {
    const { searchResults, resultsPerPage } = this.state;
    return Math.ceil(searchResults.length / resultsPerPage);
  }

  render() {
    const { searchQuery, searchResults, loading, error, searchPerformed, currentPage } = this.state;

    // Get tag from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const tagParam = urlParams.get('tag');

    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            mb: { xs: 2, sm: 3 },
            color: '#6c0c46',
            fontWeight: 600,
            fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          Search Documents
        </Typography>

        {/* Search form */}
        <Paper 
          component="form" 
          elevation={2}
          sx={{ 
            p: '8px 12px', 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid rgba(108, 12, 70, 0.2)',
            '&:focus-within': {
              borderColor: '#6c0c46',
              boxShadow: '0 0 0 2px rgba(108, 12, 70, 0.1)'
            }
          }}
          onSubmit={this.handleSearchSubmit}
        >
          <InputBase
            sx={{ 
              ml: 1, 
              flex: 1,
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}
            placeholder="Search documents..."
            value={searchQuery}
            onChange={this.handleSearchChange}
            inputProps={{ 'aria-label': 'search documents' }}
          />
          <IconButton 
            type="submit" 
            sx={{ 
              p: '8px',
              color: '#6c0c46',
              '&:hover': {
                backgroundColor: 'rgba(108, 12, 70, 0.1)'
              }
            }} 
            aria-label="search"
          >
            <SearchIcon />
          </IconButton>
        </Paper>

        {tagParam && (
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mb: 1,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Filtering by tag:
            </Typography>
            <Chip
              label={tagParam}
              onDelete={() => {
                const url = new URL(window.location);
                url.searchParams.delete('tag');
                window.location.href = url.toString();
              }}
              sx={{
                backgroundColor: 'rgba(108, 12, 70, 0.1)',
                color: '#6c0c46',
                '& .MuiChip-deleteIcon': {
                  color: '#6c0c46'
                }
              }}
            />
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: '#6c0c46' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : searchPerformed && searchResults.length === 0 ? (
          <Typography 
            sx={{ 
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              py: 4
            }}
          >
            No documents found matching your search.
          </Typography>
        ) : searchResults.length > 0 ? (
          <>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} 
              {searchResults.length > this.state.resultsPerPage && 
                ` • Page ${currentPage} of ${this.getTotalPages()}`
              }
            </Typography>
            
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {this.getPaginatedResults().map((doc) => (
                <Grid item xs={12} key={doc.id}>
                  <Card
                    elevation={2}
                    sx={{
                      transition: 'all 0.3s ease-in-out',
                      borderRadius: 2,
                      border: '1px solid rgba(108, 12, 70, 0.1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(108, 12, 70, 0.15)',
                        borderColor: 'rgba(108, 12, 70, 0.2)'
                      },
                    }}
                  >
                    <CardActionArea component={RouterLink} to={`/documents/${doc.id}`}>
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography 
                          variant="h6" 
                          component="div" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#6c0c46',
                            mb: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                          }}
                        >
                          <MarkdownText>{doc.Term || 'Untitled'}</MarkdownText>
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}
                        >
                          Updated: {this.formatDate(doc.updatedAt)}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mt: 1,
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            lineHeight: 1.5,
                            '& mark': {
                              backgroundColor: 'rgba(108, 12, 70, 0.2)',
                              color: '#6c0c46',
                              padding: '2px 4px',
                              borderRadius: '4px'
                            }
                          }}
                        >
                          <div dangerouslySetInnerHTML={{ 
                            __html: this.highlightText(
                              doc.Definition ? doc.Definition.substring(0, 200) + (doc.Definition.length > 200 ? '...' : '') : 'No definition available', 
                              searchQuery
                            )
                          }} />
                        </Typography>
                        
                        {doc.tags && doc.tags.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            {doc.tags.map(tag => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                component={RouterLink}
                                to={`/search?tag=${encodeURIComponent(tag)}`}
                                clickable
                                sx={{ 
                                  mr: 0.5,
                                  mb: 0.5,
                                  backgroundColor: 'rgba(108, 12, 70, 0.08)',
                                  color: '#6c0c46',
                                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                  height: { xs: 20, sm: 24 },
                                  '&:hover': {
                                    backgroundColor: 'rgba(108, 12, 70, 0.15)'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
              
              {this.getTotalPages() > 1 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
                    <Pagination 
                      count={this.getTotalPages()} 
                      page={currentPage} 
                      onChange={this.handlePageChange}
                      color="primary"
                      size="small"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: '#6c0c46',
                          '&.Mui-selected': {
                            backgroundColor: '#6c0c46',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#5a0a3a'
                            }
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(108, 12, 70, 0.1)'
                          }
                        }
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </>
        ) : null}
      </Container>
    );
  }
}

export default SearchPage;
